import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../api";
import "./FacilitiesMap.css";

const TYPES = {
  "ospedale": { symbol: "H", color: "#c0392b" },
  "policlinico": { symbol: "+", color: "#326ac0" },
  "ambulatorio": { symbol: "A", color: "#168575" },
  "trauma center": { symbol: "T", color: "#d97706" },
  "cau": { symbol: "C", color: "#c24162" },
  "ospedale di comunità": { symbol: "H", color: "#54853d" },
  "centro medico": { symbol: "+", color: "#8057b2" },
  "altro": { symbol: "●", color: "#697586" }
};

const DEFAULT_TYPE = { symbol: "●", color: "#697586" };

function getTypeStyle(type) {
  return TYPES[String(type || "").trim().toLocaleLowerCase("it-IT")] || DEFAULT_TYPE;
}

function validCoordinates(f) {
  if (
    f.latitude === null || f.latitude === undefined ||
    f.longitude === null || f.longitude === undefined ||
    f.latitude === "" || f.longitude === ""
  ) return false;

  const lat = Number(f.latitude);
  const lon = Number(f.longitude);

  return Number.isFinite(lat) && Number.isFinite(lon) &&
    lat >= -90 && lat <= 90 &&
    lon >= -180 && lon <= 180;
}

const iconCache = new Map();

function markerIcon(type) {
  const style = getTypeStyle(type);
  const key = `${style.symbol}:${style.color}`;

  if (!iconCache.has(key)) {
    iconCache.set(key, L.divIcon({
      className: "facility-map-marker-wrapper",
      html: `<div class="facility-map-marker" style="background:${style.color}"><span>${style.symbol}</span></div>`,
      iconSize: [36, 44],
      iconAnchor: [18, 42],
      popupAnchor: [0, -40]
    }));
  }

  return iconCache.get(key);
}

function FitBounds({ facilities }) {
  const map = useMap();

  useEffect(() => {
    if (!facilities.length) {
      map.setView([44.5, 11.35], 8);
      return;
    }

    const bounds = L.latLngBounds(
      facilities.map(f => [Number(f.latitude), Number(f.longitude)])
    );

    if (bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [35, 35],
        maxZoom: 14
      });
    }
  }, [facilities, map]);

  return null;
}

export default function FacilitiesMap() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filtri della mappa: selezione multipla e ricerca testuale.
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");

  useEffect(() => {
    let active = true;

    api.get("/facilities/map")
      .then(response => {
        if (!active) return;
        if (!Array.isArray(response.data)) {
          throw new Error("Formato dati delle strutture non valido.");
        }
        setFacilities(response.data);
      })
      .catch(err => {
        if (active) {
          setError(
            err.response?.data?.detail ||
            err.message ||
            "Impossibile caricare le strutture."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, []);

  const mapped = useMemo(
    () => facilities.filter(f => f.active !== false && validCoordinates(f)),
    [facilities]
  );

  // Opzioni ricavate esclusivamente dalle strutture geolocalizzate.
  const provinces = useMemo(
    () => [...new Set(mapped.map(f => f.province).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, "it")),
    [mapped]
  );

  const cities = useMemo(
    () => [...new Set(
      mapped
        .filter(f =>
          selectedProvinces.length === 0 ||
          selectedProvinces.includes(f.province)
        )
        .filter(f => f.city)
        .map(f => JSON.stringify([f.province || "", f.city]))
    )].sort((a, b) => {
      const [provinceA, cityA] = JSON.parse(a);
      const [provinceB, cityB] = JSON.parse(b);
      return cityA.localeCompare(cityB, "it") ||
        provinceA.localeCompare(provinceB, "it");
    }),
    [mapped, selectedProvinces]
  );

  const facilityTypes = useMemo(
    () => [...new Set(mapped.map(f => f.type).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, "it")),
    [mapped]
  );

  const availableServices = useMemo(() => {
    const services = new Map();

    mapped.forEach(f => {
      (f.services || []).forEach(service => {
        if (service.id && service.name) {
          services.set(service.id, service);
        }
      });
    });

    return [...services.values()]
      .sort((a, b) => a.name.localeCompare(b.name, "it"));
  }, [mapped]);

  const filtered = useMemo(
    () => mapped.filter(f => {
      if (
        selectedProvinces.length &&
        !selectedProvinces.includes(f.province)
      ) return false;

      if (
        selectedCities.length &&
        !selectedCities.includes(
          JSON.stringify([f.province || "", f.city])
        )
      ) return false;

      if (
        selectedTypes.length &&
        !selectedTypes.includes(f.type)
      ) return false;

      if (
        selectedServices.length &&
        !(f.services || []).some(service =>
          selectedServices.includes(service.id)
        )
      ) return false;

      if (
        nameFilter.trim() &&
        !String(f.name || "").toLocaleLowerCase("it-IT")
          .includes(nameFilter.trim().toLocaleLowerCase("it-IT"))
      ) return false;

      return true;
    }),
    [
      mapped,
      selectedProvinces,
      selectedCities,
      selectedTypes,
      selectedServices,
      nameFilter
    ]
  );

  // Gestione delle selezioni multiple.
  const toggleSelection = (setter, value) => {
    setter(previous =>
      previous.includes(value)
        ? previous.filter(item => item !== value)
        : [...previous, value]
    );
  };

  // Modificando le province, rimuove i comuni non più pertinenti.
  const toggleProvince = province => {
    const nextProvinces = selectedProvinces.includes(province)
      ? selectedProvinces.filter(item => item !== province)
      : [...selectedProvinces, province];

    setSelectedProvinces(nextProvinces);

    const validCities = new Set(
      mapped
        .filter(f =>
          nextProvinces.length === 0 ||
          nextProvinces.includes(f.province)
        )
        .filter(f => f.city)
        .map(f => JSON.stringify([f.province || "", f.city]))
    );

    setSelectedCities(previous =>
      previous.filter(city => validCities.has(city))
    );
  };

  const resetFilters = () => {
    setSelectedProvinces([]);
    setSelectedCities([]);
    setSelectedTypes([]);
    setSelectedServices([]);
    setNameFilter("");
    setServiceSearch("");
  };

  const legend = useMemo(
    () => [...new Set(filtered.map(f => f.type || "Altro"))]
      .sort((a, b) => a.localeCompare(b, "it")),
    [filtered]
  );

  if (loading) {
    return <div className="facilities-map-message">Caricamento mappa delle strutture...</div>;
  }

  if (error) {
    return <div className="facilities-map-message facilities-map-error" role="alert">{String(error)}</div>;
  }

  return (
    <section className="facilities-map-section" data-testid="facilities-map-section">
      <div className="facilities-map-heading">
        <h2>Mappa delle strutture</h2>
        <span>
          {filtered.length} di {mapped.length} strutture geolocalizzate
        </span>
      </div>

      {mapped.length > 0 && (
        <div className="facilities-map-filters">
          <div className="facilities-map-filters-heading">
            <h3>Filtra strutture</h3>
            <button type="button" onClick={resetFilters}>
              Azzera filtri
            </button>
          </div>

          <div className="facilities-map-filters-grid">
            <div className="facilities-map-filter">
              <label htmlFor="map-name-filter">Nome struttura</label>
              <input
                id="map-name-filter"
                type="search"
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                placeholder="Cerca una struttura..."
              />
            </div>

            <div className="facilities-map-filter">
              <span className="facilities-map-filter-label">Provincia</span>
              <details>
                <summary>
                  {selectedProvinces.length
                    ? `Province selezionate: ${selectedProvinces.length}`
                    : "Tutte le province"}
                </summary>
                <div className="facilities-map-filter-options">
                  {provinces.map(province => (
                    <label key={province}>
                      <input
                        type="checkbox"
                        checked={selectedProvinces.includes(province)}
                        onChange={() => toggleProvince(province)}
                      />
                      <span>{province}</span>
                    </label>
                  ))}
                </div>
              </details>
            </div>

            <div className="facilities-map-filter">
              <span className="facilities-map-filter-label">Comune</span>
              <details>
                <summary>
                  {selectedCities.length
                    ? `Comuni selezionati: ${selectedCities.length}`
                    : "Tutti i comuni"}
                </summary>
                <div className="facilities-map-filter-options">
                  {cities.map(value => {
                    const [province, city] = JSON.parse(value);
                    return (
                      <label key={value}>
                        <input
                          type="checkbox"
                          checked={selectedCities.includes(value)}
                          onChange={() =>
                            toggleSelection(setSelectedCities, value)
                          }
                        />
                        <span>{city} {province ? `(${province})` : ""}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
            </div>

            <div className="facilities-map-filter">
              <span className="facilities-map-filter-label">Tipologia</span>
              <details>
                <summary>
                  {selectedTypes.length
                    ? `Tipologie selezionate: ${selectedTypes.length}`
                    : "Tutte le tipologie"}
                </summary>
                <div className="facilities-map-filter-options">
                  {facilityTypes.map(type => (
                    <label key={type}>
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type)}
                        onChange={() =>
                          toggleSelection(setSelectedTypes, type)
                        }
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </details>
            </div>

            <div className="facilities-map-filter">
              <span className="facilities-map-filter-label">
                Servizi associati
              </span>
              <details>
                <summary>
                  {selectedServices.length
                    ? `Servizi selezionati: ${selectedServices.length}`
                    : "Tutti i servizi"}
                </summary>
                <div className="facilities-map-filter-options">
                  <input
                    type="search"
                    value={serviceSearch}
                    onChange={e => setServiceSearch(e.target.value)}
                    placeholder="Cerca un servizio..."
                    aria-label="Cerca nella lista dei servizi"
                  />
                  {availableServices
                    .filter(service =>
                      service.name
                        .toLocaleLowerCase("it-IT")
                        .includes(
                          serviceSearch.toLocaleLowerCase("it-IT")
                        )
                    )
                    .map(service => (
                      <label key={service.id}>
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(service.id)}
                          onChange={() =>
                            toggleSelection(
                              setSelectedServices,
                              service.id
                            )
                          }
                        />
                        <span>{service.name}</span>
                      </label>
                    ))}
                </div>
              </details>
            </div>
          </div>

          {filtered.length === 0 && (
            <p className="facilities-map-filter-empty" role="status">
              Nessuna struttura corrisponde ai filtri selezionati.
            </p>
          )}
        </div>
      )}

      {mapped.length === 0 ? (
        <div className="facilities-map-message">
          Nessuna struttura attiva dispone attualmente di coordinate geografiche valide.
        </div>
      ) : (
        <>
          <div className="facilities-map-container">
            <MapContainer
              center={[44.5, 11.35]}
              zoom={8}
              scrollWheelZoom={true}
              className="facilities-map-leaflet"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />

              <FitBounds facilities={filtered} />

              {filtered.map(f => {
                const latitude = Number(f.latitude);
                const longitude = Number(f.longitude);
                const mapUrl =
                  `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}` +
                  `#map=16/${latitude}/${longitude}`;

                return (
                  <Marker
                    key={f.id}
                    position={[latitude, longitude]}
                    icon={markerIcon(f.type)}
                  >
                    <Popup>
                      <div className="facilities-map-popup">
                        <strong>{f.name}</strong>
                        <span>{f.type || "Altra tipologia"}</span>
                        <p>
                          {[f.address, f.city, f.province]
                            .filter(Boolean).join(", ")}
                        </p>
                        {f.phone && <p>Tel. {f.phone}</p>}
                        <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                          Apri su OpenStreetMap
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>

          <div className="facilities-map-legend" aria-label="Legenda tipologie strutture">
            <strong>Legenda tipologie</strong>
            <div className="facilities-map-legend-items">
              {legend.map(type => {
                const style = getTypeStyle(type);
                return (
                  <div className="facilities-map-legend-item" key={type}>
                    <span
                      className="facilities-map-legend-symbol"
                      style={{ backgroundColor: style.color }}
                    >
                      {style.symbol}
                    </span>
                    <span>{type}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
